import * as React from "react";
import Grid from "@material-ui/core/Grid";
import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card as MuiCard
} from "@material-ui/core";
import { Link, useHistory } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { Button, IconButton } from "@material-ui/core";
import appLogo from "../../../src/assets/logo.png"; // renamed for clarity
import backIcon from "../../../src/assets/icons/backIcon.svg";
import SearchIcon from "../Icons/SearchIcon";
import SettingIcon from "../Icons/SettingIcon";
import LaunchIcon from "../Icons/LaunchIcon";
import RightArrowIcon from "../Icons/RightArrowIcon";
import { useStyles } from "./style";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  archiveChannelPost,
  getChannelActivePost,
  getChannelPostList,
  readChannelPost,
  unArchiveChannelPost
} from "../../redux/actions/programAction";
import { useLocation } from "react-router-dom";
import {
  closeProgramFormModal,
  getProgramFormContextData,
  getProgramFormStructure,
  openProgramFormModal
} from "../../redux/actions/programFormAction";
import { closeEnrollmentLoading } from "../../redux/actions/programEnrollmentAction";
import ActionItemFormModal from "../../screens/OnBoarding/Screens/Components/ActionItemFormModal";
import ChannelMenu from "../../screens/User/Sidebar/component/Menu";
import { formatDate, getSecondaryPostContent } from "../../util/lib";
import PostAttachments from "./components/PostAttachment";
import { useIsMobile } from "../../util/hooks";
import { handleDrawer } from "../../redux/actions/drawerAction";
import { assetUrl } from "../../util/assetUrl";
import { usePrivileges } from "../../util/usePrivileges";
import { canEditOrArchivePost } from "../../util/permissions";
import {
  DEFAULT_CHANNEL_THEME,
  sanitizeChannelTheme,
  invertHexColor,
  isHexColorLight,
  ChannelTheme
} from "../../util/theme";
// TODO: Per-post notifications - Currently disabled
// import PostBellMenu from "../PostBellMenu";

/* ===== API/Asset bases ===== */
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || API_BASE;

/* ===== Resolve stored/relative/absolute paths to usable URLs ===== */
const resolveHeaderUrl = (p: string): string => {
  if (!p) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(p)) return p;
  if (p.startsWith("/assets") || p.startsWith("/uploads")) return `${ASSET_BASE}${p}`;
  try {
    return assetUrl(p);
  } catch {
    return p;
  }
};

function PostScreen() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const urlParams = React.useMemo(
    () => new URLSearchParams(window.location.search),
    [window.location.search]
  );
  const channelIdentifier = urlParams.get("channelIdentifier");
  const urlContextId = urlParams.get("contextId"); // Get contextId from URL
  const isAdmin = location.pathname.startsWith("/admin");
  const authInfo = JSON.parse(localStorage.getItem("authInfo"));
  const userId = authInfo?.["_id"];
  const { formStructure } = useSelector((state: RootState) => state.programFormReducer);
  const { programChannelPost, postDetails, singleProgram } = useSelector((state: RootState) => state.programReducer);
  const formModal = useSelector((state: RootState) => state.programFormReducer.formModal);
  const [contextId, setContextId]=  React.useState(postDetails?.['contextId']);
  const [channelSetting, setChannelSetting]=React.useState(postDetails?.['channelPostingSettings']);
  const [selectedSourceFormId, setSelectedSourceFormId] = React.useState(postDetails?.['sourceFormId']); // <-- Store selected form ID
  const programId = singleProgram?._id;
  const recentPosts: any = programChannelPost[channelIdentifier];
  const isMobile = useIsMobile();

  // Get channel theme
  const channelTheme = useSelector((state: RootState) =>
    state.channelThemeReducer[channelIdentifier]
  );

  const activeCommunity = React.useMemo(() => {
    if (!singleProgram || !postDetails?.communityIdentifier) return undefined;
    return singleProgram.communities?.find(
      (c: any) => c.communityIdentifier === postDetails.communityIdentifier
    );
  }, [singleProgram, postDetails?.communityIdentifier]);

  const activeChannel = React.useMemo(() => {
    if (!activeCommunity || !postDetails?.channelIdentifier) return undefined;
    return activeCommunity.channels?.find(
      (ch: any) => ch.channelIdentifier === postDetails.channelIdentifier
    );
  }, [activeCommunity, postDetails?.channelIdentifier]);

  const resolvedTheme: ChannelTheme = React.useMemo(
    () =>
      sanitizeChannelTheme(
        DEFAULT_CHANNEL_THEME,
        activeCommunity?.communityStyle,
        activeChannel?.channelStyle,
        channelTheme
      ),
    [activeCommunity?.communityStyle, activeChannel?.channelStyle, channelTheme]
  );

  const { mainColor, secondaryColor, headerImage, fontStyle } = resolvedTheme;

  const invertedSecondary = React.useMemo(
    () => invertHexColor(secondaryColor),
    [secondaryColor]
  );

  // Load post when coming from URL with contextId (e.g., from email link)
  React.useEffect(() => {
    const currentContextId = (postDetails as any)?.contextId;
    console.log('[PostScreen] URL Loading Effect:', {
      urlContextId,
      channelIdentifier,
      programId,
      userId,
      currentContextId,
      postDetails: !!postDetails,
      shouldLoad: urlContextId && channelIdentifier && programId && userId && (!postDetails || currentContextId !== urlContextId)
    });
    if (urlContextId && channelIdentifier && programId && userId && (!postDetails || currentContextId !== urlContextId)) {
      // Check if we need to load channel posts first
      const channelPosts = programChannelPost[channelIdentifier];
      console.log('[PostScreen] Channel posts:', { channelIdentifier, postsCount: channelPosts?.length });

      if (!channelPosts || !Array.isArray(channelPosts) || channelPosts.length === 0) {
        // Channel posts not loaded yet - need to load them first
        // Get the community identifier from the active enrollment
        const activeEnrollment = localStorage.getItem('activeProgramEnrollment');
        if (activeEnrollment) {
          const enrollment = JSON.parse(activeEnrollment);
          const communityIdentifier = enrollment.communityIdentifier;
          if (communityIdentifier) {
            // Load channel posts
            dispatch(getChannelPostList(programId, userId, communityIdentifier, channelIdentifier, false) as any);
          }
        }
      } else {
        // Channel posts already loaded - find the specific post
        const foundPost = channelPosts.find((post: any) => post.contextId === urlContextId);
        console.log('[PostScreen] Searching for post:', { urlContextId, foundPost: !!foundPost });
        if (foundPost) {
          // Post found, set it as active
          console.log('[PostScreen] Setting active post:', foundPost.contextId);
          dispatch(getChannelActivePost(foundPost));
        } else {
          console.warn('[PostScreen] Post not found in channel posts!', {
            urlContextId,
            availableContextIds: channelPosts.map((p: any) => p.contextId)
          });
        }
      }
    }
  }, [urlContextId, channelIdentifier, programId, userId, programChannelPost, dispatch]);

  React.useEffect(() => {
    const isRead = postDetails?.userData?.isRead;
    if (programId && userId && contextId && !isRead) {
      dispatch(readChannelPost(programId, userId, contextId));
    }
  }, [dispatch, programId, userId, contextId, postDetails?.userData?.isRead]);

  React.useEffect(()=>{
    dispatch(handleDrawer(!channelIdentifier));
  },[dispatch, isMobile, channelIdentifier]);

  React.useEffect(() => {
    setSelectedSourceFormId(postDetails?.["sourceFormId"]);
    setContextId(postDetails?.["contextId"]);
    setChannelSetting(postDetails?.["channelPostingSettings"]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [postDetails]);

  // Apply channel theme when post details arrive
  React.useEffect(() => {
    if (!channelIdentifier) return;
    dispatch({
      type: "SET_CHANNEL_THEME",
      channelId: channelIdentifier,
      payload: resolvedTheme,
    });
  }, [
    dispatch,
    channelIdentifier,
    resolvedTheme.mainColor,
    resolvedTheme.secondaryColor,
    resolvedTheme.headerImage,
    resolvedTheme.fontStyle
  ]);

  const getPrimaryPostContent = (data: any) =>
    data?.title || data?.heading || data?.label;

  const getRandomPosts = (posts: any[], count: number) => {
    if (!posts || posts.length === 0) return [];
    const shuffled = [...posts].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  };

  const randomPosts = React.useMemo(
    () => getRandomPosts(recentPosts, 5),
    [recentPosts]
  );

  const handleEdit = (e: React.MouseEvent<HTMLLIElement>) => {
    dispatch(getProgramFormContextData(programId, selectedSourceFormId, userId, contextId));
    dispatch(getProgramFormStructure(programId, selectedSourceFormId, userId));
    dispatch(openProgramFormModal());
  };

  const handleArchive = (e: React.MouseEvent<HTMLLIElement>) => {
    const pd: any = postDetails;
    pd["isContextArchived"] = true;
    dispatch(archiveChannelPost(programId, contextId));
    dispatch(getChannelActivePost(pd));
  };

  const handleUnArchive = (e: React.MouseEvent<HTMLLIElement>) => {
    dispatch(unArchiveChannelPost(programId, contextId));
    const pd: any = postDetails;
    pd["isContextArchived"] = false;
    dispatch(getChannelActivePost(pd));
  };

  const { privileges } = usePrivileges(postDetails?.communityIdentifier);
  const postingSettings = channelSetting;

  const canModifyPost = React.useMemo(() => {
    if (!postDetails?.channelIdentifier || !postDetails?.communityIdentifier) return false;
    if (!programId) return false;
    return canEditOrArchivePost({
      enrollments: privileges.activeEnrollments,
      ctx: { programId, communityIdentifier: postDetails.communityIdentifier },
      channelIdentifier: postDetails.channelIdentifier,
      channelPostingSettings: postingSettings,
    });
  }, [privileges.activeEnrollments, postDetails?.channelIdentifier, postDetails?.communityIdentifier, postingSettings, programId]);

  const menuItems = [
    ...(canModifyPost ? [{ label: "Edit", onClick: handleEdit }] : []),
    ...(canModifyPost
      ? [
          postDetails?.["isContextArchived"]
            ? { label: "UnArchive", onClick: handleUnArchive }
            : { label: "Archive",   onClick: handleArchive }
        ]
      : []),
  ];

  const handleCloseModal = () => {
    dispatch(closeProgramFormModal());
    dispatch(closeEnrollmentLoading());
  };

  /* =======================
     🔧 COMMUNITY LOGO HOOKS
     (Must be before any early return)
     ======================= */
  const communityLogoRaw = React.useMemo(() => {
    const comm = singleProgram?.communities?.find(
      (c: any) => c?.communityIdentifier === postDetails?.communityIdentifier
    );
    return comm?.communityStyle?.logo || "";
  }, [singleProgram, postDetails?.communityIdentifier]);

  const [logoBust, setLogoBust] = React.useState(0);
  React.useEffect(() => {
    setLogoBust(Date.now());
  }, [communityLogoRaw]);

  const communityLogoUrl = React.useMemo(() => {
    const u = resolveHeaderUrl(communityLogoRaw);
    if (!u) return "";
    return `${u}${u.includes("?") ? "&" : "?"}v=${logoBust}`;
  }, [communityLogoRaw, logoBust]);

  /* ===== Early returns BELOW all hooks ===== */
  if (!postDetails || !postDetails.data) {
    return null;
  }

  if (!activeCommunity) {
    history.push(isAdmin ? "/admin/channel" : "/user/channel");
    return (
      <div className={classes.container}>
        <MuiCard className={classes.card}>
          <Typography variant="h5" color="error">
            Error: Community data not found for this post.
          </Typography>
        </MuiCard>
      </div>
    );
  }

  if (!activeChannel) {
    return (
      <div className={classes.container}>
        <MuiCard className={classes.card}>
          <Typography variant="h5" color="error">
            Error: Channel data not found for this post.
          </Typography>
        </MuiCard>
      </div>
    );
  }

  const activeForm = activeChannel.channelFormSettings.forms.find(
    (f) => f.sourceFormId === postDetails.sourceFormId
  );
  const formSettings = activeForm;

  const handlViewMore = () => {
    history.push(isAdmin ? "/admin/channel" : "/user/channel");
  };

  // Enhanced styling based on channel theme
  const containerStyle = {
    backgroundColor: secondaryColor,
    color: invertedSecondary,
    fontFamily: fontStyle,
    minHeight: "100vh",
  };

  const cardStyle = {
    backgroundColor: secondaryColor,
    border: `1px solid ${invertedSecondary}22`,
    boxShadow: `0 4px 20px ${invertedSecondary}15`,
    borderRadius: "12px",
    overflow: "hidden",
  };

  const headerStyle = {
    position: "relative" as const,
    height: "120px",
    background: headerImage
      ? `linear-gradient(135deg, ${mainColor}88, ${mainColor}44), url("${headerImage}")`
      : `linear-gradient(135deg, ${mainColor}, ${secondaryColor === "#ffffff" ? "#e3f2fd" : mainColor}dd)`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    padding: "20px",
  };

  const headerOverlayStyle = {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))`,
  };

  const backButtonStyle = {
    position: "absolute" as const,
    top: "20px",
    left: "20px",
    background: "rgba(255,255,255,0.2)",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    color: "white",
    cursor: "pointer",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
  };

  const channelBrandingStyle = {
    position: "relative" as const,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: "15px",
    marginLeft: "60px",
  };

  const channelTitleStyle = {
    color: "white",
    fontSize: "20px",
    fontWeight: 600,
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
    margin: 0,
  };

  const postContentStyle = {
    padding: "25px",
    borderBottom: `1px solid ${invertedSecondary}22`,
  };

  const postTitleStyle = {
    fontSize: "24px",
    fontWeight: 700,
    marginBottom: "15px",
    color: mainColor,
  };

  const postBodyStyle = {
    color: invertedSecondary,
    lineHeight: 1.6,
    marginBottom: "15px",
  };

  const timeTextStyle = {
    fontSize: "14px",
    color: `${invertedSecondary}88`,
    marginBottom: "20px",
  };

  const recentPostsSectionStyle = {
    padding: "25px",
  };

  const sectionTitleStyle = {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "15px",
    color: mainColor,
  };

  const postItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "15px",
    marginBottom: "10px",
    borderRadius: "8px",
    backgroundColor: `${invertedSecondary}08`,
    transition: "all 0.3s ease",
    cursor: "pointer",
    border: "none",
  };

  const postIconStyle = {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    color: "white",
    backgroundColor: mainColor,
  };

  const viewMoreButtonStyle = {
    backgroundColor: mainColor,
    color: isHexColorLight(mainColor) ? "#000" : "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 24px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    textTransform: "none" as const,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "15px",
  };

  const actionButtonsStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "15px",
  };

  return (
    <>
      <div style={containerStyle}>
        <MuiCard style={cardStyle}>
          {/* Enhanced Header */}
          <div style={headerStyle}>
            <div style={headerOverlayStyle}></div>
            <Link
              style={backButtonStyle}
              to={`/user/channel?channelIdentifier=${channelIdentifier}`}
            >
              <img
                style={{ width: 20, objectFit: "contain", flexShrink: 0, marginRight: 8, height: 20, filter: "brightness(0) invert(1)" }}
                src={backIcon}
                alt="Back"
              />
              {/* 🔧 Prefer community logo; fall back to app logo */}
              <img
                style={{ width: 72, height: 24, flexShrink: 0
                  //, filter: "brightness(0) invert(1)" 
                  }}
                src={communityLogoUrl || resolveHeaderUrl(appLogo)}
                alt="Logo"
              />
            </Link>
            <div style={channelBrandingStyle}>
              <Typography style={channelTitleStyle}>
                {postDetails.communityIdentifier}
              </Typography>
            </div>
          </div>

          {/* Channel Info Bar */}
          <Box style={{ padding: "15px 25px", borderBottom: `1px solid ${invertedSecondary}22` }}>
            <div style={actionButtonsStyle}>
              <Typography
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: mainColor,
                  cursor: "pointer"
                }}
                onClick={(e) => { e.stopPropagation(); history.push("/user/channel"); }}
              >
                #{postDetails.channelIdentifier}
              </Typography>
              {/* TODO: Per-Post Notification Bell - Currently Disabled
                *
                * What it would be:
                * - A bell icon button next to the channel name that allows users to subscribe
                *   to notifications for THIS SPECIFIC POST only
                * - Users could set frequency (immediate/daily/off) for updates on this post
                * - Users could mute this specific post temporarily (e.g., for 24 hours)
                * - Per-post subscriptions have HIGHEST PRIORITY in the notification policy
                *   precedence system (priority #5 - higher than channel subscriptions #6)
                *
                * Why it's disabled:
                * - Per-post notifications are less commonly needed than channel notifications
                * - Most users prefer channel-level controls (all posts in #announcements)
                * - UI simplification - reduces clutter on post page
                * - Backend support is fully implemented and tested
                *
                * How to re-enable:
                * 1. Uncomment the import: import PostBellMenu from "../PostBellMenu"
                * 2. Uncomment the component below: <PostBellMenu postId={contextId} />
                * 3. Test the UI to ensure the bell icon displays correctly
                * 4. Verify that clicking it opens the per-post subscription menu
                *
                * Component: /frontend/src/components/PostBellMenu/index.tsx
                * Backend API: POST /v1/notification-settings/post-subscriptions/:postId
                */}
              {/* <PostBellMenu postId={contextId} /> */}
              {canModifyPost && (
                <ChannelMenu menuItems={menuItems} source="post" />
              )}
              <Link to={isAdmin ? "/admin/channel" : "/user/channel"}>
                <SearchIcon />
              </Link>
              <Link to={`/user/channel/setting`}>
                <SettingIcon />
              </Link>
            </div>
          </Box>

          {/* Post Content */}
          <div style={postContentStyle}>
            <Typography style={postTitleStyle}>
              {getPrimaryPostContent(postDetails.data)}
            </Typography>
            <div
              style={postBodyStyle}
              dangerouslySetInnerHTML={{ __html: getSecondaryPostContent(postDetails.data) }}
            />
            {(postDetails.data as any).attachments && (
              <PostAttachments attachments={(postDetails.data as any).attachments} />
            )}
            <Typography style={timeTextStyle}>
              {formatDate(postDetails.createdOn)}
            </Typography>
          </div>

          <Divider variant="middle" style={{ borderColor: `${invertedSecondary}22` }} />

          {/* Recent Posts */}
          <div style={recentPostsSectionStyle}>
            <Typography style={sectionTitleStyle}>Recent Posts</Typography>
            <List style={{ padding: 0 }}>
              {randomPosts.map((post) => (
                <ListItem
                  key={post.contextId}
                  style={postItemStyle}
                  onClick={() => dispatch(getChannelActivePost(post))}
                >
                  <ListItemIcon style={{ minWidth: "auto" }}>
                    <div style={postIconStyle}>
                      <i className="material-icons" style={{ fontSize: "16px" }}>
                        {post.iconClass || "note"}
                      </i>
                    </div>
                  </ListItemIcon>
                  <ListItemText
                    primary={getPrimaryPostContent(post.data)}
                    secondary={getPrimaryPostContent(post.data)}
                    primaryTypographyProps={{
                      style: {
                        color: invertedSecondary,
                        fontWeight: 600,
                        marginBottom: "4px"
                      },
                    }}
                    secondaryTypographyProps={{
                      style: {
                        color: `${invertedSecondary}88`,
                        fontSize: "12px"
                      },
                    }}
                    style={{ margin: 0 }}
                  />
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(getChannelActivePost(post));
                      history.push(
                        `/user/post?contextId=${post.contextId}&channelIdentifier=${post.channelIdentifier}`
                      );
                    }}
                    style={{ color: mainColor, padding: "8px" }}
                  >
                    <LaunchIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
            <Button
              variant="contained"
              style={viewMoreButtonStyle}
              onClick={handlViewMore}
            >
              View More <RightArrowIcon />
            </Button>
          </div>
        </MuiCard>
      </div>

      {(formStructure !== null && formStructure !== undefined) && (
        <ActionItemFormModal
          isModal={formModal}
          closeModal={handleCloseModal}
          programId={programId}
          userId={userId}
          sourceFormId={selectedSourceFormId}
          channelIdentifier={postDetails.channelIdentifier}
          communityIdentifier={postDetails?.communityIdentifier}
          formType={'update'}
          contextId={contextId}
          formSettings={formSettings}
          setSelectedSourceFormId={setSelectedSourceFormId}
        />
      )}

      {formStructure != null && (
        <ActionItemFormModal
          isModal={formModal}
          closeModal={handleCloseModal}
          programId={programId}
          userId={userId}
          sourceFormId={selectedSourceFormId}
          channelIdentifier={postDetails.channelIdentifier}
          communityIdentifier={postDetails.communityIdentifier}
          formType="update"
          contextId={contextId}
          formSettings={formSettings}
          setSelectedSourceFormId={setSelectedSourceFormId}
        />
      )}
    </>
  );
}

export default PostScreen;
